import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import { useMatch } from '../../contexts/MatchContext';
import * as api from '../../api';
import { getAllDivisions, getAllCategories } from '../../utils/scoring';
import { wrappableHtml } from '../../utils/html';
import moment from 'moment';

function AnimatedNumber({ value, duration = 500, sx }) {
  const [display, setDisplay] = useState(0);
  const rafRef = useRef();

  useEffect(() => {
    if (value === 0) { setDisplay(0); return; }
    const start = performance.now();
    const animate = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      setDisplay(Math.round(progress * value));
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      }
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [value, duration]);

  return <Typography sx={sx}>{display}</Typography>;
}

export default function MatchHome() {
  const { t } = useTranslation();
  const { match, announcements } = useMatch();
  const navigate = useNavigate();
  const [stages, setStages] = useState(null);
  const [shooters, setShooters] = useState(null);
  const [squadAvailability, setSquadAvailability] = useState(null);
  const [announcementsExtended, setAnnouncementsExtended] = useState(false);
  const [breakdownDivision, setBreakdownDivision] = useState(null);

  useEffect(() => {
    if (!match) return;
    window.scrollTo(0, 0);

    if (match.stages?.length > 0) {
      api.getStages(match.stages.join(',')).then((res) => setStages(res.data));
    } else {
      setStages([]);
    }

    // Squad capacity for the "registration full" hint; works with unpublished squads
    if (['open', 'period-over-can-patch'].includes(match.registration)) {
      api.getSquadAvailability(match.id).then((res) => setSquadAvailability(res.data?.squads || [])).catch(() => {});
    }

    api.getShooters(match.id).then((res) => {
      setShooters((res.data || []).filter((s) => !s.cancelledParticipation));
    });
  }, [match?.id]);

  if (!match?.title) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <CircularProgress size={75} />
      </Box>
    );
  }

  const { about, squadsPublished, stagesPublished, scoresPublished } = match;

  const squadsFull = squadAvailability
    ? squadAvailability.length > 0 && squadAvailability.every((sq) => sq.full)
    : false;

  const CtaCard = ({ title, description, linkTo, ctaLabel, published, disabledTooltip, subtitle }) => (
    <Paper elevation={3} sx={{ textAlign: 'center', p: 1.5, width: 300, m: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Typography sx={{ fontSize: '1.2em', mb: 0.75 }}>{title}</Typography>
      <Typography variant="body2" sx={{ fontSize: '0.8em', height: 30, mb: 3, color: 'text.secondary' }}>{description}</Typography>
      <Button
        component={Link}
        to={linkTo}
        variant="contained"
        color="primary"
        disabled={!published}
        sx={{ width: '60%', mb: 1.5, textTransform: 'none' }}
      >
        {ctaLabel}
      </Button>
      {!published && disabledTooltip && (
        <Typography variant="caption" color="text.secondary">{disabledTooltip}</Typography>
      )}
      {published && subtitle && (
        <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
      )}
    </Paper>
  );

  return (
    <Box>
      {/* About Section */}
      {about && (
        <Box sx={{ pt: 1.5, pb: 3, mb: 3, borderBottom: '1px solid rgba(0,0,0,0.2)' }}>
          <Box dangerouslySetInnerHTML={{ __html: wrappableHtml(about) }} sx={{ px: 3, maxWidth: '100%', overflowWrap: 'break-word', wordBreak: 'break-word', overflow: 'hidden' }} />
        </Box>
      )}

      {/* Announcements */}
      {announcements.length > 0 && (
        <Box sx={{
          pt: about ? 0 : 1.5,
          pb: 3,
          mb: 3,
          textAlign: 'center',
          borderBottom: '1px solid rgba(0,0,0,0.2)',
        }}>
          {(announcementsExtended ? announcements : [announcements[0]]).map((announcement) => (
            <Paper key={announcement.id} sx={{ m: 'auto', mt: 1.5, width: '90%', maxWidth: 640, textAlign: 'left' }}>
              <Box sx={{
                color: 'white', p: 1.5, display: 'flex', fontSize: 18,
                bgcolor: 'primary.main', alignItems: 'center',
              }}>
                <Box sx={{ flex: 1 }}>{announcement.subject}</Box>
                <Typography variant="caption" sx={{ color: 'white' }}>
                  {moment(announcement.created).format('lll')}
                </Typography>
              </Box>
              <Box
                sx={{
                  p: 1.5,
                  fontSize: 13,
                  fontWeight: 300,
                  maxWidth: '100%',
                  overflowWrap: 'break-word',
                  wordBreak: 'break-word',
                  overflow: 'hidden',
                  '& img': { maxWidth: '100%', height: 'auto' },
                }}
                dangerouslySetInnerHTML={{ __html: wrappableHtml(announcement.body) }}
              />
            </Paper>
          ))}

          {announcements.length > 1 && !announcementsExtended && (
            <Box sx={{ textAlign: 'left', width: '90%', maxWidth: 640, mx: 'auto', mt: 0.75 }}>
              <Typography
                variant="caption"
                sx={{ color: 'rgba(0,0,0,0.7)', cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => setAnnouncementsExtended(true)}
              >
                {t('matchHome.moreAnnouncements', { count: announcements.length - 1 })}
              </Typography>
            </Box>
          )}

          {announcements.length > 1 && announcementsExtended && (
            <Box sx={{ textAlign: 'left', width: '90%', maxWidth: 640, mx: 'auto', mt: 0.75 }}>
              <Typography
                variant="caption"
                sx={{ color: 'rgba(0,0,0,0.7)', cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => setAnnouncementsExtended(false)}
              >
                {t('matchHome.lessAnnouncements')}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Stats Section */}
      {squadsPublished && stagesPublished && (
        <Box sx={{
          pt: (about || announcements.length > 0) ? 0 : 1.5,
          pb: 3, mb: 3,
          borderBottom: '1px solid rgba(0,0,0,0.2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap',
        }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', px: 9 }}>
            <AnimatedNumber value={stages ? stages.length : 0} sx={{ fontSize: 84, lineHeight: 1 }} />
            <Typography variant="body1">{t('matches.stages')}</Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', px: 9 }}>
            <AnimatedNumber value={stages ? stages.reduce((count, s) => count + (s.rounds || 0), 0) : 0} sx={{ fontSize: 84, lineHeight: 1 }} />
            <Typography variant="body1">{t('matchHome.rounds')}</Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', px: 9 }}>
            <AnimatedNumber value={shooters ? shooters.length : 0} sx={{ fontSize: 84, lineHeight: 1 }} />
            <Typography variant="body1">{t('matches.shooters')}</Typography>
          </Box>
        </Box>
      )}

      {/* Division Breakdown */}
      {squadsPublished && stagesPublished && shooters && shooters.length > 20 && (
        <Box sx={{
          pb: 3, mb: 3,
          borderBottom: '1px solid rgba(0,0,0,0.2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap',
        }}>
          {getAllDivisions({ matchType: match.type }).map((division) => {
            const count = shooters.filter((s) => s.division === division).length;
            if (count === 0) return null;
            return (
              <Button
                key={division}
                onClick={() => setBreakdownDivision(breakdownDivision === division ? null : division)}
                color={breakdownDivision === division ? 'primary' : 'inherit'}
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <Typography variant="body2" sx={{ fontWeight: 'bold', mr: 0.5 }}>{count}</Typography>
                <Typography variant="body2">{t(`divisions.${division.replace(/-([a-z])/g, (_, c) => c.toUpperCase())}`, { defaultValue: division })}</Typography>
              </Button>
            );
          })}
        </Box>
      )}

      {/* Category Breakdown for selected division */}
      {breakdownDivision && shooters && (
        <Box sx={{
          pb: 3, mb: 3,
          borderBottom: '1px solid rgba(0,0,0,0.2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap',
        }}>
          {getAllCategories({ matchType: match.type }).map((category) => {
            const count = shooters.filter((s) => s.division === breakdownDivision && s.category === category).length;
            if (count === 0) return null;
            return (
              <Box key={category} sx={{ display: 'flex', alignItems: 'center', px: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mr: 0.5 }}>{count}</Typography>
                <Typography variant="body2">{t(`categories.${category.replace(/-([a-z])/g, (_, c) => c.toUpperCase())}`, { defaultValue: category })}</Typography>
              </Box>
            );
          })}
        </Box>
      )}

      {/* CTA Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', mb: 2 }}>
        <CtaCard
          title={t('matchHome.squadsTitle')}
          description={t('matchHome.squadsDesc')}
          linkTo={`/${match.alias}/squads`}
          ctaLabel={t('matchHome.squadsCta')}
          published={squadsPublished}
          disabledTooltip={!squadsPublished ? t('matchHome.squadsDisabled') : null}
        />
        <CtaCard
          title={t('matchHome.shootersTitle')}
          description={t('matchHome.shootersDesc')}
          linkTo={`/${match.alias}/shooters`}
          ctaLabel={t('matchHome.shootersCta')}
          published={squadsPublished}
          disabledTooltip={!squadsPublished ? t('matchHome.shootersDisabled') : null}
        />
        <CtaCard
          title={t('matchHome.stagesTitle')}
          description={t('matchHome.stagesDesc')}
          linkTo={`/${match.alias}/stages`}
          ctaLabel={t('matchHome.stagesCta')}
          published={stagesPublished}
          disabledTooltip={!stagesPublished ? t('matchHome.stagesDisabled') : null}
        />
        {match.registration !== 'closed' && (
          <CtaCard
            title={t('matchHome.registrationTitle')}
            description={t('matchHome.registrationDesc')}
            linkTo={`/${match.alias}/registration`}
            ctaLabel={t('matchHome.registrationCta')}
            published={['open', 'open-code', 'period-over-can-patch'].includes(match.registration)}
            disabledTooltip={
              match.registration === 'sold-out' ? t('matchHome.registrationSoldOut')
              : match.registration === 'period-over' ? t('matchHome.registrationPeriodOver')
              : null
            }
            subtitle={squadsFull ? t('matchHome.registrationFull') : null}
          />
        )}
        {(match.roRegistration === 'open' || match.roRegistration === 'open-code') && (
          <CtaCard
            title={t('matchHome.roRegistrationTitle', 'RO Registration')}
            description={t('matchHome.roRegistrationDesc', 'Sign up as a range officer for this match')}
            linkTo={`/${match.alias}/ro-registration`}
            ctaLabel={t('matchHome.roRegistrationCta', 'Sign up as RO')}
            published
          />
        )}
        <CtaCard
          title={t('matchHome.resultsTitle')}
          description={t('matchHome.resultsDesc')}
          linkTo={`/${match.alias}/results`}
          ctaLabel={t('matchHome.resultsCta')}
          published={!!scoresPublished}
          disabledTooltip={!scoresPublished ? t('matchHome.resultsDisabled') : null}
        />
      </Box>
    </Box>
  );
}
